/**
 * File: DateRangePicker.tsx
 * Path: apps/admin/src/features/dashboard/components/DateRangePicker.tsx
 * Description: Simplified date boundary selection utilizing a single input trigger.
 * The End Date is implicitly locked to the current date, while the calendar dynamically highlights the lookback range.
 */

import React from "react";
import { Calendar } from "./Calendar";
import { cn } from "@idk/utils";
import { useDateRangePickerController } from "@/features/dashboard/hooks/useDateRangePickerController";
import { DATE_PRESETS, formatDisplayDate, type DateRange } from "@/features/dashboard/utils/dateRangeUtils";

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  disabled?: boolean;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { isOpen, today, containerRef, activePreset, selectedRange, toggle, close, applyPreset, selectCalendarDay, apply } =
    useDateRangePickerController(value, onChange);

  return (
    <div ref={containerRef} className="relative inline-block text-left z-40">
      {/* Unified Single Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={toggle}
        className="flex items-start bg-transparent py-stack-xs text-foreground transition-all duration-300 outline-none border-none cursor-pointer group disabled:cursor-not-allowed disabled:opacity-50"
      >
        <div className="flex flex-col items-start min-w-[140px]">
          <span className="text-xs font-normal uppercase tracking-wider text-muted-foreground mb-stack-xs block h-3 leading-none transition-colors group-hover:text-foreground">
            analysis start
          </span>
          <span className="text-sm font-normal tracking-wide text-foreground/90 border-b border-border/30 pb-1 w-full text-left transition-colors group-hover:border-foreground/40">
            {activePreset === "custom"
              ? formatDisplayDate(value.startDate)
              : `Last ${activePreset} Days`}
          </span>
        </div>
      </button>

      {/* Popover Menu */}
      {isOpen && !disabled && (
        <div className="absolute right-0 origin-top-right mt-stack-md z-50 flex rounded-none border border-border/20 bg-card/95 backdrop-blur-md shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 overflow-hidden">
          {/* Calendar & Actions Area */}
          <div className="flex flex-col">
            {/* Using mode="range" to provide the visual sweeping effect from the selected Start Date up to Today. 
              We override the standard range selection behavior by intercepting the `selectedDay` in `onSelect`.
            */}
            <Calendar
              mode="range"
              selected={selectedRange}
              onSelect={(_range, selectedDay) => selectCalendarDay(selectedDay)}
              disabled={{ after: today }} // Prevent selecting dates in the future
              numberOfMonths={1}
              className="p-gutter-sm"
            />

            <div className="flex items-center justify-end gap-x-stack-md border-t border-border/20 p-gutter-sm bg-muted/5">
              <button
                onClick={close}
                className="text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={apply}
                className="text-xs font-medium uppercase tracking-wider bg-foreground text-background px-gutter-md py-stack-xs rounded-none hover:opacity-90 transition-opacity"
              >
                Apply Range
              </button>
            </div>
          </div>

          {/* Preset Sidebar */}
          <div className="flex flex-col min-w-[140px] border-r border-border/20 bg-muted/10 p-gutter-sm gap-y-1">
            <span className="text-xs font-normal uppercase tracking-wider text-muted-foreground mb-stack-md pl-stack-sm">
              Presets
            </span>
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() =>
                  applyPreset(parseInt(preset.label), preset.label)
                }
                className={cn(
                  "text-left text-sm font-normal tracking-wide px-stack-sm py-stack-xs rounded-none transition-colors duration-200",
                  activePreset === preset.label
                    ? "bg-foreground/5 text-foreground"
                    : "text-muted-foreground hover:bg-foreground/[0.02] hover:text-foreground",
                )}
              >
                {preset.text}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
